import React, { useState, useRef, useEffect } from "react";
import Card from "components/card";
import { MdSearch, MdError, MdWarehouse, MdInventory, MdLocationOn, MdClose, MdQrCode } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const FinishZone = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedZone, setSelectedZone] = useState(null);
  const [showZoneDetails, setShowZoneDetails] = useState(false);
  const [selectedZoneForQR, setSelectedZoneForQR] = useState(null);
  const [showQRPreview, setShowQRPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zones, setZones] = useState([]);
  const qrCodeRef = useRef();

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
      
      // Get unique lanes from the API data
      const uniqueLanes = new Set(response.data.data.flatMap(product => 
        product.batch_details.map(batch => {
          // Use the full lane name if available, otherwise use zone
          if (batch.lane) {
            return batch.lane;
          }
          return `Lane ${batch.zone}`;
        })
      ));

      // Group products by lane
      const lanesMap = response.data.data.reduce((acc, product) => {
        product.batch_details.forEach(batch => {
          // Use the full lane name as the key
          const laneKey = batch.lane || `Lane ${batch.zone}`;
          
          if (!acc[laneKey]) {
            acc[laneKey] = {
              id: laneKey,
              name: laneKey,
              totalProducts: 0,
              totalQuantity: 0,
              products: []
            };
          }
          
          // Only add products and batches with non-zero quantity
          if (parseInt(batch.batch_actual_stock) > 0) {
            // Check if product type already exists in this lane
            const existingProduct = acc[laneKey].products.find(p => p.name === product.product_type);
            if (existingProduct) {
              existingProduct.batches.push({
                batchId: batch.batch_id,
                quantity: parseInt(batch.batch_actual_stock),
                slot: batch.slot || batch.rack,
                lane: batch.lane || `Lane ${batch.zone}`,
                status: batch.status,
                julian_date: batch.julian_date
              });
            } else {
              acc[laneKey].products.push({
                name: product.product_type,
                batches: [{
                  batchId: batch.batch_id,
                  quantity: parseInt(batch.batch_actual_stock),
                  slot: batch.slot || batch.rack,
                  lane: batch.lane || `Lane ${batch.zone}`,
                  status: batch.status,
                  julian_date: batch.julian_date
                }]
              });
              acc[laneKey].totalProducts++;
            }
            
            acc[laneKey].totalQuantity += parseInt(batch.batch_actual_stock);
          }
        });
        return acc;
      }, {});

      // Create lanes array from the API data
      const zones = Array.from(uniqueLanes).map(laneName => ({
        id: laneName,
        name: laneName,
        totalProducts: lanesMap[laneName]?.totalProducts || 0,
        totalQuantity: lanesMap[laneName]?.totalQuantity || 0,
        products: lanesMap[laneName]?.products || []
      })).sort((a, b) => {
        // Sort by lane number, then by A/B designation
        const aMatch = a.id.match(/Lane (\d+)(\s*[AB])?/);
        const bMatch = b.id.match(/Lane (\d+)(\s*[AB])?/);
        if (aMatch && bMatch) {
          const aNum = parseInt(aMatch[1]);
          const bNum = parseInt(bMatch[1]);
          if (aNum !== bNum) return aNum - bNum;
          // If same number, A comes before B
          const aSuffix = aMatch[2] || '';
          const bSuffix = bMatch[2] || '';
          return aSuffix.localeCompare(bSuffix);
        }
        return a.id.localeCompare(b.id);
      });

      setZones(zones);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching zones:', error);
      setError('Failed to fetch zones data');
      setLoading(false);
      toast.error('Failed to fetch zones data');
    }
  };

  // Filter lanes based on search query
  const filteredZones = zones.filter(zone => 
    zone.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewDetails = (zone) => {
    navigate(`/admin/finished-products/zones/${zone.id}`, { state: { zone } });
  };

  // Function to generate QR code for a zone
  const generateZoneQRCode = (zone) => {
    return new Promise((resolve, reject) => {
      setSelectedZoneForQR(zone);
      
      setTimeout(() => {
        if (qrCodeRef.current) {
          const svgElement = qrCodeRef.current.querySelector('svg');
          
          if (svgElement) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            canvas.width = 400;
            canvas.height = 500;
            
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            const svgData = new XMLSerializer().serializeToString(svgElement);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const DOMURL = window.URL || window.webkitURL || window;
            const img = new Image();
            const svgUrl = DOMURL.createObjectURL(svgBlob);
            
            img.onload = function() {
              ctx.drawImage(img, 80, 70, 240, 240);
              
              ctx.fillStyle = 'black';
              ctx.font = 'bold 24px Arial';
              ctx.textAlign = 'center';
              ctx.fillText('Zone QR Code', canvas.width/2, 40);
              
              ctx.fillStyle = '#f8f8f8';
              ctx.fillRect(50, 330, canvas.width - 100, 150);
              
              ctx.fillStyle = 'black';
              ctx.font = 'bold 20px Arial';
              ctx.fillText(`${zone.name}`, canvas.width/2, 400);
              
              ctx.font = '16px Arial';
              ctx.fillText('Scan to view zone details', canvas.width/2, 480);
              
              canvas.toBlob(function(blob) {
                const url = URL.createObjectURL(blob);
                resolve({
                  url,
                  filename: `Zone-${zone.name}-QRCode.png`
                });
              });
              
              DOMURL.revokeObjectURL(svgUrl);
            };
            
            img.src = svgUrl;
          } else {
            reject(new Error('SVG element not found'));
          }
        } else {
          reject(new Error('QR code ref not found'));
        }
      }, 100);
    });
  };

  // Function to handle QR code preview
  const handleQRCodePreview = (zone) => {
    setSelectedZoneForQR(zone);
    setShowQRPreview(true);
  };

  // Function to handle QR code download
  const handleQRCodeDownload = async () => {
    try {
      const { url, filename } = await generateZoneQRCode(selectedZoneForQR);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      toast.success('QR code downloaded successfully');
      setShowQRPreview(false);
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const renderZoneDetailsModal = () => {
    if (!showZoneDetails || !selectedZone) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setShowZoneDetails(false)}></div>
        
        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-4">
          <Card extra="p-6 max-w-2xl w-full relative">
            {/* Close Button */}
            <button
              onClick={() => setShowZoneDetails(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <MdClose size={24} />
            </button>

            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <MdWarehouse className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{selectedZone.name}</h3>
                  <p className="text-sm text-gray-500">{selectedZone.totalProducts} product types • {selectedZone.totalQuantity} boxes total</p>
                </div>
              </div>
            </div>

            {/* Products List */}
            <div className="space-y-4">
              {selectedZone.products.map((product) => (
                <div
                  key={product.name}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <MdInventory className="text-blue-600" size={20} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{product.name}</span>
                        <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                          {product.batches.map(batch => batch.batchId).join(', ')}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <MdLocationOn size={16} className="mr-1" />
                        <span>{product.batches[0]?.lane || `Lane ${selectedZone.id}`}, Slot {product.batches[0]?.slot}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-semibold text-blue-600">{product.batches.reduce((total, batch) => total + batch.quantity, 0)} boxes</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-5xl text-red-500">
            <MdError className="mx-auto h-16 w-16" />
          </div>
          <p className="text-xl font-semibold text-gray-700 dark:text-white">
            {error}
          </p>
          <button
            onClick={fetchZones}
            className="mt-6 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-600 hover:shadow-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-6"}>
        {/* Header and Search */}
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
                          <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
              Finished Product Lanes
            </h4>
            <span className="rounded-full bg-brand-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm">
              {filteredZones.length} lanes
            </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1">
                <MdInventory className="h-4 w-4 text-blue-600" />
                <span className="text-gray-500">Total Products:</span>
                <span className="font-semibold text-blue-600">
                  {filteredZones.reduce((total, zone) => total + zone.totalQuantity, 0)} boxes
                </span>
              </span>
            </div>
          </div>
          <div className="flex w-full items-center rounded-xl border bg-white px-4 py-2.5 shadow-sm transition-all duration-200 hover:shadow-md dark:bg-navy-700 md:w-[420px]">
            <MdSearch className="h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search lanes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none placeholder:text-gray-400 dark:text-white"
            />
          </div>
        </div>

        {/* Lanes Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredZones.map((zone) => (
            <Card
              key={zone.id}
              extra="p-5 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-navy-700"
            >
              <div className="flex flex-col gap-5">
                {/* Zone Header */}
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-500">
                    {zone.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleQRCodePreview(zone)}
                      className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-600"
                      title="View QR Code"
                    >
                      <MdQrCode className="h-5 w-5" />
                    </button>
                    <span className="rounded-full bg-gray-50 px-4 py-1.5 text-sm font-medium text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                      {zone.totalProducts} products
                    </span>
                  </div>
                </div>

                {/* Zone Content */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Total Quantity</span>
                    <span className="font-semibold text-navy-700 dark:text-white text-lg">
                      {zone.totalQuantity} boxes
                    </span>
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm text-gray-500">Products</span>
                    <div className="flex flex-wrap gap-2">
                      {zone.products.slice(0, 3).map((product) => (
                        <span
                          key={product.name}
                          className="rounded-lg bg-gray-50 px-3 py-1 text-sm font-medium text-gray-600 dark:bg-navy-700 dark:text-gray-300"
                        >
                          {product.name}: {product.batches.reduce((total, batch) => total + batch.quantity, 0)} boxes
                        </span>
                      ))}
                      {zone.products.length > 3 && (
                        <span className="rounded-lg bg-gray-50 px-3 py-1 text-sm font-medium text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                          +{zone.products.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => handleViewDetails(zone)}
                  className="w-full rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-600 hover:shadow-lg"
                >
                  View Details
                </button>
              </div>
            </Card>
          ))}
        </div>

        {/* No Results Message */}
        {filteredZones.length === 0 && (
          <div className="mt-6 flex h-[200px] items-center justify-center rounded-xl border border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-800">
            <p className="text-center text-gray-500 dark:text-gray-400">
              No lanes found matching your search
            </p>
          </div>
        )}
      </Card>

      {/* Zone Details Modal */}
      {renderZoneDetailsModal()}

      {/* QR Code Preview Modal */}
      {showQRPreview && selectedZoneForQR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <div className="relative w-[420px] rounded-2xl bg-white p-8 shadow-2xl dark:bg-navy-700 transform transition-all duration-300 ease-in-out">
            <button
              onClick={() => setShowQRPreview(false)}
              className="absolute right-4 top-4 rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-600 transition-colors duration-200"
            >
              <MdClose className="h-5 w-5" />
            </button>
            <div className="flex flex-col items-center gap-6">
              <div className="flex flex-col items-center gap-2">
                <h3 className="text-2xl font-bold text-navy-700 dark:text-white">Lane QR Code</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Scan to view lane details</p>
              </div>
              <div className="rounded-xl border-2 border-gray-100 bg-white p-6 shadow-lg dark:border-navy-600 dark:bg-navy-800">
                <QRCodeSVG 
                  value={`https://usibras-web.vercel.app/admin/finished-products/zones/${selectedZoneForQR.id.replace('Lane ', '')}`}
                  size={240} 
                  level={"H"} 
                  includeMargin={true} 
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Lane</span>
                <span className="text-xl font-bold text-navy-700 dark:text-white">{selectedZoneForQR.name}</span>
              </div>
              <button
                onClick={handleQRCodeDownload}
                className="w-full rounded-xl bg-brand-500 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-600 hover:shadow-lg active:scale-[0.98]"
              >
                Download QR Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden div for QR code generation */}
      {selectedZoneForQR && (
        <div style={{ display: 'none' }} ref={qrCodeRef}>
          <QRCodeSVG 
            value={`https://usibras-web.vercel.app/admin/finished-products/zones/${selectedZoneForQR.id.replace('Lane ', '')}`}
            size={240} 
            level={"H"} 
            includeMargin={true} 
          />
        </div>
      )}
    </div>
  );
};

export default FinishZone; 